import { z } from "zod";

import { useSyncQuery } from "src/graphql";

const webhookTestConfigSchema = z
  .object({
    add: z
      .object({
        enabled: z.literal(true),
        bodyType: z.literal("mappings"),
        bodyUseAllColumns: z.literal(false).optional(),
        body: z.array(
          z
            .object({
              to: z.string(),
              type: z.string(),
              from: z.string().optional(),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  })
  .passthrough();

export async function loadWebhookTestSyncConfig({
  syncId,
  destinationId,
  externalId,
}: {
  syncId: string;
  destinationId: string;
  externalId: string;
}): Promise<Record<string, unknown>> {
  const { syncs } = await useSyncQuery.fetcher({ id: syncId })();
  const sync = syncs[0];
  if (
    !sync ||
    sync.destination?.id !== destinationId ||
    sync.destination.type !== "webhookV2"
  ) {
    throw new Error(
      "The linked webhook sync is unavailable. Check the channel's sync configuration and try again.",
    );
  }

  // Require an explicit recipient mapping so tests use the entered ID.
  const result = webhookTestConfigSchema.safeParse(sync.config);
  if (!result.success) {
    throw new Error(
      'Webhook tests require an enabled "Rows added" request with mapped body fields. Check the linked sync configuration.',
    );
  }

  const recipientMappings = result.data.add.body.filter(
    (mapping) => mapping.to === externalId,
  );
  if (
    recipientMappings.length !== 1 ||
    recipientMappings[0]?.type !== "standard" ||
    recipientMappings[0]?.from !== "external_id"
  ) {
    throw new Error(
      `Map external_id to ${externalId} in the linked sync's request body before sending a test.`,
    );
  }

  return result.data;
}
